import React, { useEffect, useRef, useState } from "react";
import { fetchClips, incrementClipView } from "../lib/api";
import { Crown } from "./CrownIcon";
import { Play, Eye, Pause, Volume2, VolumeX } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const resolveUrl = (u) => {
    if (!u) return u;
    if (u.startsWith("http://") || u.startsWith("https://")) return u;
    return `${BACKEND_URL}${u.startsWith("/") ? "" : "/"}${u}`.replace(
        /\/api$/,
        "",
    );
};

const tones = ["green", "pink", "blue"];
const toneStyle = {
    green: {
        shadow: "shadow-[10px_10px_0_#53FC18]",
        text: "text-kickgreen",
        bg: "bg-kickgreen",
    },
    pink: {
        shadow: "shadow-[10px_10px_0_#FF2E93]",
        text: "text-hotpink",
        bg: "bg-hotpink",
    },
    blue: {
        shadow: "shadow-[10px_10px_0_#0AC3FF]",
        text: "text-skyblue",
        bg: "bg-skyblue",
    },
};

export const ClipsSection = () => {
    const [clips, setClips] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchClips()
            .then((d) => setClips(d))
            .catch(() => setClips([]))
            .finally(() => setLoading(false));
    }, []);

    return (
        <section
            id="clips"
            data-testid="clips-section"
            className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24"
        >
            <div
                aria-hidden
                className="absolute inset-0 overflow-hidden pointer-events-none"
            >
                <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full bg-hotpink/15 blur-3xl" />
                <div className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full bg-skyblue/15 blur-3xl" />
            </div>

            <div className="relative flex items-end justify-between gap-4 mb-10 flex-wrap">
                <div>
                    <div className="inline-flex items-center gap-2 mb-3">
                        <Crown className="w-9 h-7" color="#53FC18" />
                        <span className="font-anton uppercase tracking-[0.3em] text-xs text-kickgreen">
                            Freshest Drops
                        </span>
                    </div>
                    <h2
                        data-testid="clips-title"
                        className="font-anton uppercase text-5xl md:text-7xl text-white leading-[0.9] stamp-sm"
                    >
                        The <span className="text-hotpink">Clips</span>
                        <br />
                        <span className="text-skyblue">Profile</span>
                    </h2>
                    <p className="font-graffiti text-kickgreen text-2xl -rotate-2 mt-2">
                        newest first ↓
                    </p>
                </div>
                <p className="font-chivo text-white/60 text-sm max-w-md">
                    Best moments from the late-night stream. Tap any clip to play —
                    share the ones that hit different.
                </p>
            </div>

            <div className="relative">
                {loading ? (
                    <div className="font-chivo text-white/40 text-center py-16">
                        Loading clips…
                    </div>
                ) : clips.length === 0 ? (
                    <div className="font-chivo text-white/40 text-center py-16">
                        No clips yet. Stay tuned.
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                        {clips.map((clip, i) => (
                            <ClipCard
                                key={clip.id}
                                clip={clip}
                                tone={tones[i % tones.length]}
                                index={i}
                                autoplay={i === 0}
                            />
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
};

const ClipCard = ({ clip, tone, index, autoplay = false }) => {
    const videoRef = useRef(null);
    const [playing, setPlaying] = useState(false);
    const [muted, setMuted] = useState(autoplay);
    const [viewed, setViewed] = useState(false);
    const t = toneStyle[tone];
    const rot = index % 2 === 0 ? "rotate-[-1.5deg]" : "rotate-[1.5deg]";

    useEffect(() => {
        if (!autoplay) return;
        const el = videoRef.current;
        if (!el) return;
        el.muted = true;
        el.play()
            .then(() => {
                setPlaying(true);
                if (!viewed) {
                    setViewed(true);
                    incrementClipView(clip.id).catch(() => {});
                }
            })
            .catch(() => {
                /* autoplay blocked — overlay stays */
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoplay, clip.id]);

    const toggle = async () => {
        const el = videoRef.current;
        if (!el) return;
        if (el.paused) {
            try {
                el.muted = muted;
                await el.play();
                setPlaying(true);
                if (!viewed) {
                    setViewed(true);
                    incrementClipView(clip.id).catch(() => {});
                }
            } catch {
                /* ignore autoplay rejections */
            }
        } else {
            el.pause();
            setPlaying(false);
        }
    };

    const toggleMute = (e) => {
        e.stopPropagation();
        const el = videoRef.current;
        const next = !muted;
        setMuted(next);
        if (el) el.muted = next;
    };

    return (
        <article
            data-testid="clip-card"
            className={`group relative ${rot} hover:rotate-0 transition-transform duration-500`}
        >
            {/* Animated gradient outer frame */}
            <div className="clip-frame p-1.5">
                <div className={`relative bg-black border-2 border-black ${t.shadow}`}>
                    {/* Tape strip top-left */}
                    <div
                        aria-hidden
                        className="absolute -top-2 left-3 z-20 h-3 w-16 tape"
                    />
                    {/* Number sticker */}
                    <div
                        aria-hidden
                        className={`absolute -top-3 right-3 z-20 ${t.bg} text-black font-anton uppercase text-xs tracking-widest px-2 py-1 border-2 border-black rotate-[6deg]`}
                    >
                        Clip #{String(index + 1).padStart(2, "0")}
                    </div>

                    {/* Video wrap with 9:16 aspect */}
                    <div
                        className="relative bg-black overflow-hidden cursor-pointer"
                        style={{ aspectRatio: "9 / 16" }}
                        onClick={toggle}
                    >
                        <video
                            ref={videoRef}
                            data-testid="clip-video"
                            src={resolveUrl(clip.video_url)}
                            poster={resolveUrl(clip.poster_url)}
                            playsInline
                            preload={autoplay ? "auto" : "metadata"}
                            muted={muted}
                            autoPlay={autoplay}
                            loop={autoplay}
                            controls={playing && !autoplay}
                            className="absolute inset-0 w-full h-full object-cover"
                            onEnded={() => !autoplay && setPlaying(false)}
                            onPause={() => !autoplay && setPlaying(false)}
                            onPlay={() => setPlaying(true)}
                        />

                        {/* Autoplay mute toggle */}
                        {autoplay && playing && (
                            <button
                                data-testid="clip-mute-toggle"
                                aria-label={muted ? "Unmute" : "Mute"}
                                onClick={toggleMute}
                                className="absolute bottom-3 right-3 z-10 bg-black/80 text-white border-2 border-white p-2 hover:bg-kickgreen hover:text-black transition-colors"
                            >
                                {muted ? (
                                    <VolumeX className="w-4 h-4" />
                                ) : (
                                    <Volume2 className="w-4 h-4" strokeWidth={3} />
                                )}
                            </button>
                        )}
                        {autoplay && playing && muted && (
                            <div
                                aria-hidden
                                className="absolute bottom-3 left-3 z-10 bg-kickgreen text-black font-anton uppercase text-[10px] tracking-widest px-2 py-1 border-2 border-black animate-pulse"
                            >
                                Tap for sound 🔊
                            </div>
                        )}

                        {/* Play overlay before first play */}
                        {!playing && (
                            <div
                                data-testid="clip-play-overlay"
                                className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/10 transition-colors"
                            >
                                <div
                                    className={`${t.bg} text-black w-20 h-20 flex items-center justify-center border-4 border-black shadow-[6px_6px_0_#000] group-hover:scale-110 transition-transform animate-neon-chase`}
                                >
                                    <Play className="w-8 h-8 ml-1" strokeWidth={3} />
                                </div>
                            </div>
                        )}

                        {/* Pause hint while playing */}
                        {playing && (
                            <button
                                aria-label="Pause"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggle();
                                }}
                                className="absolute top-3 right-3 z-10 bg-black/70 text-white border-2 border-white p-2 hover:bg-hotpink hover:text-black transition-colors"
                            >
                                <Pause className="w-4 h-4" />
                            </button>
                        )}
                    </div>

                    {/* Footer meta strip */}
                    <div className="relative bg-black px-4 py-3 border-t-2 border-white/10 flex items-center justify-between gap-3">
                        <div className="min-w-0">
                            <div
                                className={`font-anton uppercase tracking-wider text-base ${t.text} truncate`}
                                title={clip.title}
                            >
                                {clip.title}
                            </div>
                            <div className="font-chivo text-[10px] uppercase tracking-widest text-white/40">
                                {new Date(clip.created_at).toLocaleDateString()}
                            </div>
                        </div>
                        <div className="flex items-center gap-1 font-chivo text-xs text-white/60 whitespace-nowrap">
                            <Eye className="w-3 h-3" />
                            <span data-testid="clip-views">{clip.views || 0}</span>
                        </div>
                    </div>
                </div>
            </div>
        </article>
    );
};
